import * as Constants from "../utils/constants";
import * as ApiUtils from "./api-utils";
import { StationDefinition } from "./api-data-model";
import { startOfDay, addHours, getISODay, addDays } from "date-fns";

type ODDataBase = {
  avgReliability120min: number;
  avgReliability5min: number;
  nbDelay120min: number;
  nbDelay5min: number;
//  nbSuppr: number;
  nbTrains: number;
  reliability120min: number;
  reliability5min: number;
};

export type GraphODEvolutionData = ODDataBase & {
  month: Date;
};

export type GraphODPerHourData = ODDataBase & {
  hour: Date;
};

export type GraphODPerDayData = ODDataBase & {
  day: Date;
};

export type ODAnneeData<T> = { year: string; data: T[] };

export type ODPerModeData<T> = { mode: string; years: ODAnneeData<T>[] };

export type GetODAllGraphsResult = {
  query: { from: StationDefinition; to: StationDefinition };
  modes: string[];
  years: string[];
  hours: ODAnneeData<GraphODPerHourData>[];
  hours_per_mode: ODPerModeData<GraphODPerHourData>[];
  days: ODAnneeData<GraphODPerDayData>[];
  days_per_mode: ODPerModeData<GraphODPerDayData>[];
  evolution: ODAnneeData<GraphODEvolutionData>[];
  evolutionPerMode: ODPerModeData<GraphODEvolutionData>[];
}

export type GetODEvolutionResult = {
  evolution: ODAnneeData<GraphODEvolutionData>[];
  evolutionPerMode: ODPerModeData<GraphODEvolutionData>[];
  query: { from: StationDefinition; to: StationDefinition };
};

export type GetODPerHourResult = {
  hours: ODAnneeData<GraphODPerHourData>[];
  hours_per_mode: ODPerModeData<GraphODPerHourData>[];
  query: { from: StationDefinition; to: StationDefinition };
};

export type GetODPerDayResult = {
  days: ODAnneeData<GraphODPerDayData>[];
  days_per_mode: ODPerModeData<GraphODPerDayData>[];
  query: { from: StationDefinition; to: StationDefinition };
};

export async function getOrigDestAllGraphs(
  fromId: string,
  toId: string
): Promise<GetODAllGraphsResult> {
  let url = ApiUtils.AppendParams(Constants.API_EP_GRAPH_OD_ALL_GRAPHS, {
    from: fromId,
    to: toId,
  });
  let response = await fetch(url);
  let allGraphsResult = await response.json();
  allGraphsResult.hours.forEach((year: any) => {
    year.data.forEach(function (value: any) {
      let hours = (value.hour as string).split(":");
      value.hour = startOfDay(new Date());
      value.hour = addHours(value.hour, Number(hours[0]));
    })
  });
  allGraphsResult.hours_per_mode.forEach((mode: any) => {
    mode.years.forEach((year: any) => {
      year.data.forEach(function (value: any) {
        let hours = (value.hour as string).split(":");
        value.hour = startOfDay(new Date());
        value.hour = addHours(value.hour, Number(hours[0]));
      })
    });
  });
  allGraphsResult.days.forEach((year: any) => {
    year.data.forEach(function (value: any) {
      let day = new Date();
      while (getISODay(day) !== value.day) {
        day = addDays(day, 1)
      }
      value.day = day;
    })
  });
  allGraphsResult.days_per_mode.forEach((mode: any) => {
    mode.years.forEach((year: any) => {
      year.data.forEach(function (value: any) {
        let day = new Date();
        while (getISODay(day) !== value.day) {
          day = addDays(day, 1)
        }
        value.day = day;
      })
    });
  });
  allGraphsResult.evolution.forEach((year: any) => {
    year.data.forEach(function (value: any) {
      value.month = new Date(value.month);
    })
  });
  allGraphsResult.evolutionPerMode.forEach((mode: any) => {
    mode.years.forEach((year: any) => {
      year.data.forEach(function (value: any) {
        value.month = new Date(value.month);
      })
    });
  });
  return allGraphsResult;
}

export async function getOrigDestEvolution(
  fromId: string,
  toId: string
): Promise<GetODEvolutionResult> {
  let url = ApiUtils.AppendParams(Constants.API_EP_GRAPH_OD_EVOL, {
    from: fromId,
    to: toId,
  });
  let response = await fetch(url);
  let evolutionResult = await response.json();
  evolutionResult.evolution.forEach((year: any) => {
    year.data.forEach(function (value: any) {
      value.month = new Date(value.month);
    })
  });
  evolutionResult.evolutionPerMode.forEach((mode: any) => {
    mode.years.forEach((year: any) => {
      year.data.forEach(function (value: any) {
        value.month = new Date(value.month);
      })
    });
  });
  return evolutionResult;
}

export async function getOrigDestPerHour(
  fromId: string,
  toId: string
): Promise<GetODPerHourResult> {
  let url = ApiUtils.AppendParams(Constants.API_EP_GRAPH_OD_PERHOUR, {
    from: fromId,
    to: toId,
  });
  let response = await fetch(url);
  let perHourResult = await response.json();
  perHourResult.hours.forEach((year: any) => {
    year.data.forEach(function (value: any) {
      let hours = (value.hour as string).split(":");
      value.hour = startOfDay(new Date());
      value.hour = addHours(value.hour, Number(hours[0]));
    })
  });
  perHourResult.hours_per_mode.forEach((mode: any) => {
    mode.years.forEach((year: any) => {
      year.data.forEach(function (value: any) {
        let hours = (value.hour as string).split(":");
        value.hour = startOfDay(new Date());
        value.hour = addHours(value.hour, Number(hours[0]));
      })
    });
  });
  return perHourResult;
}

export async function getOrigDestPerDay(
  fromId: string,
  toId: string
): Promise<GetODPerDayResult> {
  let url = ApiUtils.AppendParams(Constants.API_EP_GRAPH_OD_PERDAY, {
    from: fromId,
    to: toId,
  });
  let response = await fetch(url);
  let perDayResult = await response.json();
  perDayResult.days.forEach((year: any) => {
    year.data.forEach(function (value: any) {
      let day = new Date();
      while (getISODay(day) !== value.day) {
        day = addDays(day, 1)
      }
      value.day = day;
    })
  });
  perDayResult.days_per_mode.forEach((mode: any) => {
    mode.years.forEach((year: any) => {
      year.data.forEach(function (value: any) {
        let day = new Date();
        while (getISODay(day) !== value.day) {
          day = addDays(day, 1)
        }
        value.day = day;
      })
    });
  });
  return perDayResult;
}

export function getODEvolutionCSVExportLink(
  fromId: string,
  toId: string,
  mode: string
): string {
  return ApiUtils.AppendParams(Constants.API_EP_GRAPH_CSV_EVOL, {
    from: fromId,
    to: toId,
    mode: mode,
  });
}

export function getODPerHourCSVExportLink(
  fromId: string,
  toId: string,
  mode: string
): string {
  return ApiUtils.AppendParams(Constants.API_EP_GRAPH_CSV_PERHOUR, {
    from: fromId,
    to: toId,
    mode: mode,
  });
}

export function getODPerDayCSVExportLink(
  fromId: string,
  toId: string,
  mode: string
): string {
  return ApiUtils.AppendParams(Constants.API_EP_GRAPH_CSV_PERDAY, {
    from: fromId,
    to: toId,
    mode: mode,
  });
}
